import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import SearchableDropdown from '../components/SearchableDropdown';
import TaskAssigneePicker from '../components/TaskAssigneePicker';
import { SortableTh } from '../components/SortableTh';
import AppointmentCalendar, { type CalendarAppointment } from '../components/AppointmentCalendar';
import { useAuth } from '../context/AuthContext';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
import { makeAppointmentFilter } from '../utils/appointmentSearch';
import {
  createElevatorAppointment,
  fetchElevatorAppointments,
  fetchElevatorInitialSurveys,
  hasOpenConflict,
  type ElevatorAppointmentRow,
  type ElevatorInitialSurveyRow,
} from '../api/elevatorAppointments';
import { type ClientPickerRow } from '../api/clients';
import { useClientPickerOptions } from '../hooks/useClientPickerOptions';
import { fetchVerifiedUsers, type VerifiedUser } from '../api/users';
import './Page.css';
import './TasksBoard.css';
import './Events.css';
import './Elevators.css';

function formatAppointment(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function AppointmentsTable({
  title,
  sectionId,
  rows,
  emptyText,
  onRowClick,
  extraColumn,
}: {
  title: string;
  sectionId: string;
  rows: ElevatorAppointmentRow[];
  emptyText: string;
  onRowClick: (id: number) => void;
  extraColumn?: { label: string; key: string; render: (r: ElevatorAppointmentRow) => React.ReactNode };
}) {
  const headingId = `elevators-section-${sectionId}`;
  const { sortKey, sortDir, toggleSort } = useTableSortState();
  const sorted = useMemo(
    () =>
      sortRows(rows, sortKey, sortDir, (r, key) => {
        switch (key) {
          case 'job_code':
            return r.job_code ?? '';
          case 'business':
            return r.business_name ?? '';
          case 'poc':
            return r.point_of_contact ?? '';
          case 'appointment':
            return r.appointment_at ?? '';
          case 'owner':
            return r.created_by_username ?? '';
          case extraColumn?.key:
            return String(extraColumn?.render(r) ?? '');
          default:
            return '';
        }
      }),
    [rows, sortKey, sortDir, extraColumn]
  );
  const colCount = 5 + (extraColumn ? 1 : 0);
  return (
    <section className="events-table-section" aria-labelledby={headingId}>
      <h2 className="events-section-title" id={headingId}>
        {title} <span className="events-section-count">({rows.length})</span>
      </h2>
      <div className="events-table-wrap">
        <table className="tasks-search-results-table">
          <thead>
            <tr>
              <SortableTh label="Job code" columnKey="job_code" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Business" columnKey="business" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Point of contact" columnKey="poc" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Appointment" columnKey="appointment" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Created by" columnKey="owner" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              {extraColumn ? (
                <SortableTh label={extraColumn.label} columnKey={extraColumn.key} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              ) : null}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="events-empty-cell">
                  {emptyText}
                </td>
              </tr>
            ) : (
              sorted.map((r) => (
                <tr key={r.id} className="events-table-row-clickable" onClick={() => onRowClick(r.id)}>
                  <td data-label="Job code">
                    {r.job_code ? <span className="job-code">{r.job_code}</span> : '—'}
                  </td>
                  <td data-label="Business">
                    {hasOpenConflict(r) ? (
                      <span className="appt-conflict-flag" title="Client reported a scheduling conflict" aria-label="Scheduling conflict">
                        ❗
                      </span>
                    ) : null}
                    {r.business_name}
                  </td>
                  <td data-label="Point of contact">{r.point_of_contact || '—'}</td>
                  <td data-label="Appointment">{formatAppointment(r.appointment_at)}</td>
                  <td data-label="Created by">{r.created_by_username ?? '—'}</td>
                  {extraColumn ? <td data-label={extraColumn.label}>{extraColumn.render(r)}</td> : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SurveysTable({
  rows,
  onRowClick,
}: {
  rows: ElevatorInitialSurveyRow[];
  onRowClick: (id: number) => void;
}) {
  const headingId = 'elevators-section-surveys';
  const { sortKey, sortDir, toggleSort } = useTableSortState();
  const displayName = (r: ElevatorInitialSurveyRow) => {
    const v = r.form_data?.['customer_property_name'];
    return (typeof v === 'string' && v.trim()) || r.business_name || '—';
  };
  const contractLabel = (r: ElevatorInitialSurveyRow) =>
    !r.has_contract ? 'Not sent' : r.contract_signed ? 'Signed' : 'Sent · unsigned';
  const sorted = useMemo(
    () =>
      sortRows(rows, sortKey, sortDir, (r, key) => {
        switch (key) {
          case 'business':
            return displayName(r);
          case 'submitted':
            return r.submitted_at ?? '';
          case 'order':
            return r.order_number ?? '';
          case 'contract':
            return contractLabel(r);
          case 'status':
            return r.status ?? '';
          default:
            return '';
        }
      }),
    [rows, sortKey, sortDir]
  );
  return (
    <section className="events-table-section" aria-labelledby={headingId}>
      <h2 className="events-section-title" id={headingId}>
        Initial Surveys <span className="events-section-count">({rows.length})</span>
      </h2>
      <div className="events-table-wrap">
        <table className="tasks-search-results-table">
          <thead>
            <tr>
              <SortableTh label="Business / Property" columnKey="business" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Submitted" columnKey="submitted" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Order" columnKey="order" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Contract" columnKey="contract" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Status" columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={5} className="events-empty-cell">
                  No submitted surveys yet.
                </td>
              </tr>
            ) : (
              sorted.map((r) => (
                <tr key={r.id} className="events-table-row-clickable" onClick={() => onRowClick(r.id)}>
                  <td data-label="Business / Property">{displayName(r)}</td>
                  <td data-label="Submitted">{formatAppointment(r.submitted_at)}</td>
                  <td data-label="Order">{r.order_number || '—'}</td>
                  <td data-label="Contract">
                    <span className={`survey-contract-badge survey-contract-badge--${r.contract_signed ? 'signed' : r.has_contract ? 'unsigned' : 'none'}`}>
                      {contractLabel(r)}
                    </span>
                  </td>
                  <td data-label="Status">{r.status}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

type CreateSource = 'manual' | 'client';

const Elevators: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [scheduled, setScheduled] = useState<ElevatorAppointmentRow[]>([]);
  const [completed, setCompleted] = useState<ElevatorAppointmentRow[]>([]);
  const [voided, setVoided] = useState<ElevatorAppointmentRow[]>([]);
  const [surveys, setSurveys] = useState<ElevatorInitialSurveyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [source, setSource] = useState<CreateSource>('manual');
  const { clients, clientsLoading } = useClientPickerOptions();
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [pointOfContact, setPointOfContact] = useState('');
  const [pocPhone, setPocPhone] = useState('');
  const [pocEmail, setPocEmail] = useState('');
  const [appointmentAt, setAppointmentAt] = useState('');
  const [assigneeIds, setAssigneeIds] = useState<number[]>([]);
  const [verifiedUsers, setVerifiedUsers] = useState<VerifiedUser[]>([]);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState('');

  const filterRows = useMemo(
    () =>
      makeAppointmentFilter<ElevatorAppointmentRow>(searchQuery, (r) => [
        r.job_code,
        r.business_name,
        r.business_address,
        r.point_of_contact,
        r.order_number,
        r.created_by_username,
        ...r.assignees.map((a) => a.username),
      ]),
    [searchQuery]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [data, surveyList] = await Promise.all([
        fetchElevatorAppointments(),
        fetchElevatorInitialSurveys().catch(() => [] as ElevatorInitialSurveyRow[]),
      ]);
      setScheduled(data.scheduled);
      setCompleted(data.completed);
      setVoided(data.void);
      setSurveys(surveyList);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load appointments.');
      setScheduled([]);
      setCompleted([]);
      setVoided([]);
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchVerifiedUsers()
      .then((list) => {
        if (!cancelled) setVerifiedUsers(list);
      })
      .catch(() => {
        if (!cancelled) setVerifiedUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Employees with the Elevator role are listed first in the assignment picker.
  const assigneePickerUsers = useMemo(() => {
    const withElevator = (u: VerifiedUser) => (u.roles ?? []).includes('elevator');
    const ordered = [...verifiedUsers].sort((a, b) => {
      const ae = withElevator(a) ? 0 : 1;
      const be = withElevator(b) ? 0 : 1;
      if (ae !== be) return ae - be;
      return a.username.localeCompare(b.username);
    });
    return ordered.map((u) => ({ id: u.id, username: u.username }));
  }, [verifiedUsers]);

  const resetForm = () => {
    setSource('manual');
    setSelectedClientId(null);
    setBusinessName('');
    setBusinessAddress('');
    setPointOfContact('');
    setPocPhone('');
    setPocEmail('');
    setAppointmentAt('');
    setAssigneeIds([]);
    setAddError('');
  };

  const openAdd = () => {
    resetForm();
    setAddOpen(true);
  };

  const applyClient = (client: ClientPickerRow | null) => {
    if (!client) {
      setSelectedClientId(null);
      return;
    }
    setSelectedClientId(client.id);
    setBusinessName(client.company ?? '');
    setBusinessAddress(client.site_location || client.billing_address || '');
    setPointOfContact(client.point_of_contact ?? '');
    setPocPhone(client.contact_phone ?? '');
    setPocEmail(client.contact_email ?? '');
    setAddError('');
  };

  const submitAdd = async () => {
    if (!user?.id) return;
    const name = businessName.trim();
    if (!name) {
      setAddError('Name of business is required.');
      return;
    }
    if (!appointmentAt) {
      setAddError('Appointment date and time is required.');
      return;
    }
    setAddSubmitting(true);
    setAddError('');
    try {
      const created = await createElevatorAppointment({
        user_id: user.id,
        client_id: source === 'client' ? selectedClientId : null,
        business_name: name,
        business_address: businessAddress.trim() || null,
        point_of_contact: pointOfContact.trim() || null,
        poc_phone: pocPhone.trim() || null,
        poc_email: pocEmail.trim() || null,
        appointment_at: new Date(appointmentAt).toISOString(),
        assignee_user_ids: assigneeIds,
      });
      setAddOpen(false);
      navigate(`/elevators/${created.id}`);
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Failed to create appointment.');
    } finally {
      setAddSubmitting(false);
    }
  };

  const clientOptions = useMemo(() => clients.map((c) => c.company), [clients]);
  const selectedClientDisplay = useMemo(() => {
    const c = clients.find((x) => x.id === selectedClientId);
    return c?.company ?? '';
  }, [clients, selectedClientId]);

  const calendarEvents = useMemo<CalendarAppointment[]>(() => {
    return [...scheduled, ...completed]
      .filter((r) => !!r.appointment_at)
      .map((r) => ({
        id: `elevator-${r.id}`,
        date: r.appointment_at as string,
        title: r.business_name || 'Elevator appointment',
        color: '#2563eb',
        subtitle: `Elevator · ${r.status}`,
        onClick: () => navigate(`/elevators/${r.id}`),
      }));
  }, [scheduled, completed, navigate]);

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader title="Elevators" subtitle="Schedule and document elevator installer intake appointments" />
          <div className="page-content">
            {!user ? (
              <p className="page-subtitle">Log in to view appointments.</p>
            ) : (
              <>
                <div className="page-toolbar events-page-toolbar">
                  <input
                    type="text"
                    className="page-toolbar-search"
                    placeholder="Search by job code, business, contact…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search elevator appointments"
                  />
                  <button type="button" className="page-toolbar-btn" onClick={openAdd}>
                    +&nbsp; Create appointment
                  </button>
                </div>

                {loadError ? (
                  <p className="create-order-error" role="alert">
                    {loadError}
                  </p>
                ) : null}

                {loading ? (
                  <p className="page-subtitle">Loading appointments…</p>
                ) : (
                  <>
                    <section className="events-table-section" aria-label="Appointment calendar">
                      <AppointmentCalendar
                        events={calendarEvents}
                        legend={[{ label: 'Elevator appointment', color: '#2563eb' }]}
                        hint="Scheduled and completed elevator appointments. Click an appointment to open it."
                      />
                    </section>
                    <SurveysTable rows={surveys} onRowClick={(sid) => navigate(`/elevators/survey/${sid}`)} />
                    <AppointmentsTable
                      title="Scheduled Appointments"
                      sectionId="scheduled"
                      rows={filterRows(scheduled)}
                      emptyText="No scheduled appointments."
                      onRowClick={(id) => navigate(`/elevators/${id}`)}
                    />
                    <AppointmentsTable
                      title="Completed Appointments"
                      sectionId="completed"
                      rows={filterRows(completed)}
                      emptyText="No completed appointments."
                      onRowClick={(id) => navigate(`/elevators/${id}`)}
                      extraColumn={{
                        label: 'Completed',
                        key: 'completed',
                        render: (r) => formatAppointment(r.completed_at),
                      }}
                    />
                    <AppointmentsTable
                      title="Voided Appointments"
                      sectionId="void"
                      rows={filterRows(voided)}
                      emptyText="No voided appointments."
                      onRowClick={(id) => navigate(`/elevators/${id}`)}
                      extraColumn={{
                        label: 'Void reason',
                        key: 'void_reason',
                        render: (r) => r.void_reason || '—',
                      }}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <Modal isOpen={addOpen} onClose={() => setAddOpen(false)} title="Create appointment" wide>
        <div className="modal-body">
          <div className="form-group">
            <span className="form-label">Source</span>
            <div className="elevators-source-row">
              <label className="elevators-radio-label">
                <input
                  type="radio"
                  name="elevator-source"
                  checked={source === 'manual'}
                  onChange={() => {
                    setSource('manual');
                    setSelectedClientId(null);
                  }}
                  disabled={addSubmitting}
                />
                Enter business information
              </label>
              <label className="elevators-radio-label">
                <input
                  type="radio"
                  name="elevator-source"
                  checked={source === 'client'}
                  onChange={() => setSource('client')}
                  disabled={addSubmitting}
                />
                Select a client from the database
              </label>
            </div>
          </div>

          {source === 'client' ? (
            <div className="form-group">
              <label className="form-label" htmlFor="elevator-client-picker">
                Client
              </label>
              <SearchableDropdown
                options={clientOptions}
                value={selectedClientDisplay}
                onChange={(display) => {
                  const c = clients.find((x) => x.company === display) ?? null;
                  applyClient(c);
                }}
                placeholder={clientsLoading ? 'Loading clients…' : 'Type to search clients…'}
                disabled={addSubmitting || clientsLoading}
                noResultsMessage="No matching client"
              />
              {selectedClientId != null ? (
                <p className="elevators-confirm-hint">
                  Review and edit the details below, then set the appointment date and time to confirm.
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="form-group">
            <label htmlFor="elevator-business-name" className="form-label">
              Name of Business
            </label>
            <input
              id="elevator-business-name"
              type="text"
              className="form-input"
              value={businessName}
              onChange={(e) => {
                setBusinessName(e.target.value);
                setAddError('');
              }}
              placeholder="e.g. Skyline Tower Management"
              disabled={addSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="elevator-business-address" className="form-label">
              Address of Business
            </label>
            <input
              id="elevator-business-address"
              type="text"
              className="form-input"
              value={businessAddress}
              onChange={(e) => setBusinessAddress(e.target.value)}
              placeholder="Street, City, State, ZIP"
              disabled={addSubmitting}
            />
          </div>
          <div className="elevators-form-row">
            <div className="form-group">
              <label htmlFor="elevator-poc" className="form-label">
                Point of Contact
              </label>
              <input
                id="elevator-poc"
                type="text"
                className="form-input"
                value={pointOfContact}
                onChange={(e) => setPointOfContact(e.target.value)}
                disabled={addSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="elevator-poc-phone" className="form-label">
                Point of Contact's Phone Number
              </label>
              <input
                id="elevator-poc-phone"
                type="tel"
                className="form-input"
                value={pocPhone}
                onChange={(e) => setPocPhone(e.target.value)}
                disabled={addSubmitting}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="elevator-poc-email" className="form-label">
              Point of Contact's Email Address
            </label>
            <input
              id="elevator-poc-email"
              type="email"
              className="form-input"
              value={pocEmail}
              onChange={(e) => setPocEmail(e.target.value)}
              disabled={addSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="elevator-appointment-at" className="form-label">
              Appointment Date and Time
            </label>
            <input
              id="elevator-appointment-at"
              type="datetime-local"
              className="form-input"
              value={appointmentAt}
              onChange={(e) => {
                setAppointmentAt(e.target.value);
                setAddError('');
              }}
              disabled={addSubmitting}
            />
          </div>

          <div className="form-group">
            <span className="form-label">Assigned employees</span>
            <p className="elevators-confirm-hint" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
              Employees with the Elevator role are listed first. Assignees are added to the appointment's task.
            </p>
            <TaskAssigneePicker
              users={assigneePickerUsers}
              value={assigneeIds}
              onChange={setAssigneeIds}
              addPlaceholder="Search employee to assign…"
            />
          </div>

          {addError ? (
            <p className="create-order-error" role="alert">
              {addError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={addSubmitting} onClick={() => setAddOpen(false)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={addSubmitting} onClick={() => void submitAdd()}>
            {addSubmitting ? 'Creating…' : 'Confirm & create'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Elevators;
